import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import {cmsService} from '../../services/cmsService';
import SafeIcon from '../../common/SafeIcon';
import {FiCheck} from 'react-icons/fi';

const scrollToContact=()=> {
  const contactSection=document.getElementById('contact');
  if (contactSection) {
    contactSection.scrollIntoView({behavior: 'smooth'});
  }
};

const Pricing=({onSelectPackage})=> {
  const [pricing,setPricing]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=> {
    const loadPricing=async ()=> {
      const data=await cmsService.fetchPricing();
      setPricing(data);
      setLoading(false);
    };
    loadPricing();
    // Start polling for pricing updates
    const cleanup=cmsService.startPricingPolling((freshPricing)=> {
      setPricing(freshPricing);
    });
    return cleanup;
  },[]);

  const handleAction=(pkg)=> {
    if (pkg.checkoutUrl && pkg.checkoutUrl.trim() !=='') {
      window.open(pkg.checkoutUrl,'_blank','noopener,noreferrer');
    } else {
      onSelectPackage(pkg.tier);
      scrollToContact();
    }
  };

  // Updated parseFeatures to handle newlines and semicolons
  const parseFeatures=(features)=> {
    if (Array.isArray(features)) return features;
    if (typeof features==='string') {
      // Split by newlines or semicolons,trim each item,remove empty items
      return features
        .split(/[\n;]+/)
        .map(f=> f.trim())
        .filter(Boolean);
    }
    return [];
  };

  if (loading) {
    return (
      <section className="py-24 flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 rounded-full border-2 border-gold border-t-transparent animate-spin"></div>
          <p className="mt-4 font-serif text-muted tracking-widest uppercase text-xs">Loading Collections</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-20">
          <div className="luxury-section-kicker justify-center mb-6">
            <span>Packages</span>
          </div>
          <h2 className="text-4xl md:text-5xl luxury-serif-heading mb-6">Investment Options</h2>
          <p className="text-lg font-light text-muted">Select the collection that fits your current brand scale.</p>
        </div>
        {/* Grid: 1 col mobile, 2 col tablet, 2 col medium desktop, 4 col wide screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8">
          {pricing.map((tier,index)=> (
            <motion.div
              key={index}
              initial={{opacity: 0,y: 20}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{delay: index * 0.1}}
              // Flex column layout with natural height growth
              className={`luxury-panel p-8 flex flex-col relative transition-all duration-500 hover:-translate-y-2 hover:shadow-luxury luxury-hover-lift ${tier.tag?.toLowerCase()==='popular' ? 'ring-2 ring-gold shadow-goldGlow' : ''}`}
            >
              {/* Badge at top */}
              {tier.offerActive && tier.offerText && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-champagne text-espressoDeep text-[10px] uppercase tracking-widest px-6 py-2 rounded-full font-bold shadow-goldGlow whitespace-nowrap z-10">
                  {tier.offerText}
                </div>
              )}
              {tier.tag && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-champagne text-espressoDeep text-[10px] uppercase tracking-widest px-6 py-1.5 rounded-full font-bold shadow-sm z-10">
                  {tier.tag}
                </div>
              )}
              
              {/* Package Title */}
              <h3 className="text-2xl luxury-serif-heading mb-3 mt-4">{tier.tier}</h3>
              
              {/* Price display with offer */}
              {tier.offerActive && tier.originalPriceText ? (
                <div className="mb-5">
                  <div className="text-xl font-serif text-gold">{tier.priceText}</div>
                  <div className="text-sm text-muted line-through">{tier.originalPriceText}</div>
                  {tier.offerEndsAt && (
                    <div className="text-xs text-muted mt-1">
                      Offer ends {new Date(tier.offerEndsAt).toLocaleDateString('en-AU',{day: '2-digit',month: 'short',year: 'numeric'})}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xl font-serif text-gold mb-5">{tier.priceText}</div>
              )}
              
              {/* Description - removed fixed height, added proper margin */}
              <p className="text-sm font-light text-muted mb-6 leading-relaxed flex-shrink-0">
                {tier.description}
              </p>
              
              {/* Includes List - flex-grow to push CTA down, proper spacing */}
              <ul className="space-y-3 mb-6 flex-grow">
                {parseFeatures(tier.features).map((feature,i)=> (
                  <li key={i} className="flex items-start text-xs font-medium text-espresso leading-relaxed">
                    <SafeIcon icon={FiCheck} className="text-gold mr-3 mt-0.5 flex-shrink-0 w-4 h-4" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* CTA button - margin-top-auto equivalent via flex-grow above */}
              <button
                onClick={()=> handleAction(tier)}
                className={`block w-full text-center py-4 text-xs tracking-widest uppercase transition-all duration-500 font-medium rounded-full flex-shrink-0 ${tier.tag?.toLowerCase()==='popular' ? 'bg-gradient-to-r from-gold to-champagne text-espressoDeep shadow-goldGlow hover:shadow-md luxury-gold-sweep' : 'border border-gold/50 text-espresso hover:bg-gold/10'}`}
              >
                {tier.buttonText || 'Enquire Now'}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;